// Number and format utilities.
//
// The redesign surfaces import the short-name aliases at the bottom
// (`cs`, `fmtMoney`, `fmtPct`) so the ported JSX from design-handoff reads
// the same as the prototype. The longer `format*` helpers are the canonical
// implementations every other page already imports — short names just
// delegate.

export function formatCurrency(value, decimals = 2) {
  if (typeof value !== 'number' || isNaN(value)) return '$0.00';
  return '$' + value.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function formatNumber(value, decimals = 0) {
  if (typeof value !== 'number' || isNaN(value)) return '0';
  return value.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function formatCompact(value) {
  if (typeof value !== 'number' || isNaN(value)) return '0';
  if (value >= 1e12) return (value / 1e12).toFixed(1) + 'T';
  if (value >= 1e9) return (value / 1e9).toFixed(1) + 'B';
  if (value >= 1e6) return (value / 1e6).toFixed(1) + 'M';
  if (value >= 1e3) return (value / 1e3).toFixed(1) + 'K';
  return value.toFixed(0);
}

export function formatPercent(value, decimals = 2) {
  if (typeof value !== 'number' || isNaN(value)) return '0.00%';
  const sign = value >= 0 ? '+' : '';
  return sign + value.toFixed(decimals) + '%';
}

export function formatVolume(value) {
  return formatCompact(value);
}

// Format a date-only value (YYYY-MM-DD) without timezone shifting it.
// Accepts:
//   "2026-06-18"                          ← raw ISO date
//   "2026-06-18T00:00:00.000Z"            ← serialised pg DATE (UTC midnight)
//   "2026-06-18T00:00:00"                 ← naive datetime
//   Date object whose toISOString starts with the date we want
//
// Why this exists: option_expiry is a calendar date with no time component, but
// `new Date("2026-06-18").toLocaleDateString()` parses the string as UTC
// midnight and then formats it in the browser's local TZ, so users west of
// UTC see "6/17/2026" for a contract that actually expires June 18. This
// helper takes the YYYY-MM-DD prefix directly and reformats — never
// instantiates a Date that could be shifted.
export function formatDateOnly(value, { format = 'us' } = {}) {
  if (value == null || value === '') return '';
  let iso;
  if (value instanceof Date) {
    iso = value.toISOString().slice(0, 10);
  } else {
    iso = String(value).slice(0, 10);
  }
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  const [, y, mo, d] = m;
  if (format === 'iso') return `${y}-${mo}-${d}`;
  return `${mo}/${d}/${y}`; // US locale
}

// (delta-adjusted option premium estimator removed — the user is upgrading
// to Polygon Options Developer for real-time NBBO bid/ask, which makes the
// model-output Taylor estimate unnecessary and was producing confusing
// numbers vs. the actual position value on the Trade page.)

export function classifyRSI(rsi) {
  if (rsi < 30) return { label: 'Oversold', color: '#10b981' };
  if (rsi < 40) return { label: 'Weak', color: '#6ee7b7' };
  if (rsi > 70) return { label: 'Overbought', color: '#ef4444' };
  if (rsi > 60) return { label: 'Strong', color: '#fca5a5' };
  return { label: 'Neutral', color: '#f59e0b' };
}

export function classifyConfidence(conf) {
  if (conf > 80) return { label: 'Very High', color: '#10b981' };
  if (conf > 65) return { label: 'High', color: '#6ee7b7' };
  if (conf > 50) return { label: 'Moderate', color: '#f59e0b' };
  return { label: 'Low', color: '#ef4444' };
}

// ── Redesign-friendly short aliases ─────────────────────────────────────────
// These mirror the global `window.cs` / `window.fmtMoney` the design-handoff
// JSX uses, so the ported components can `import { cs, fmtMoney, fmtPct }`
// and read identically to the prototype.
export const cs = '$';

/** Always-positive USD: `fmtMoney(-1234)` → `$1,234.00`. Caller decides sign. */
export function fmtMoney(value, decimals = 2) {
  const v = Number(value);
  if (!Number.isFinite(v)) return '—';
  return cs + Math.abs(v).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** Signed percent with `−` (unicode minus) and `+`. */
export function fmtPct(value, decimals = 2) {
  const v = Number(value);
  if (!Number.isFinite(v)) return '—';
  const sign = v > 0 ? '+' : v < 0 ? '−' : '';
  return `${sign}${Math.abs(v).toFixed(decimals)}%`;
}
